import { usePermissions } from '@/hooks/usePermissions';
import LocationService from '@/services/locations';
import { useAuth0 } from '@auth0/auth0-react';
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeviceService from '../../services/devices';
import { STATUS_OPTIONS } from '@/services/turnarounds';
// import { useCustomerSitePersistence } from '../hooks/useCustomerSitePersistence';
// import AddTurnAroundModal from './AddTurnAroundModal';
import { useAppSelector } from '@/store/hooks';
import { selectSelectedGDM, selectSelectedSiteName } from '@/store/selectors';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Dayjs } from 'dayjs';
import { formatDate } from '../../utils/date';
import { CustomCheckboxIcon, CustomCheckboxIconChecked } from '../common/custom-checkbox';
import { LocationOption } from '@/types/locations';

const DEVICE_STATUS = [
  {
    'key': 'decommissioned',
    'labelKey': 'decommissioned'
  },
  {
    'key': 'is_critical',
    'labelKey': 'critical'
  },
  {
    'key': 'site_verified',
    'labelKey': 'siteVerified'
  }
];

interface TurnAroundHeaderProps {
  filterDevices?: (filters: any) => void;
  paginationModel: { page: number; pageSize: number };
  onPaginationModelChange: (model: { page: number; pageSize: number }, details?: any) => void;
  onCreateTurnaroundClick?: () => void;
}

const TurnAroundHeader = React.memo(({
  filterDevices,
  paginationModel,
  onPaginationModelChange,
  onCreateTurnaroundClick
}: TurnAroundHeaderProps) => {
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  // useCustomerSitePersistence();
  const { getFeatureActions } = usePermissions();

  const selectedSiteId = useAppSelector(selectSelectedGDM);
  const siteName = useAppSelector(selectSelectedSiteName);
  const totalCount = useAppSelector((state) => state.turnarounds.totalCount);
  const { data: userData } = useAppSelector((state) => state.user);
  const guid = useAppSelector(selectSelectedGDM);

  const showgdmnumber = getFeatureActions('Navigation', 'View Customer GDM ID', 'Customer Navigation');
  const [search, setSearch] = useState('');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showDeviceStatus, setShowDeviceStatus] = useState(false);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [selectedStatusOptions, setSelectedStatusOptions] = useState<string[]>([]);
  const [selectedDeviceStatus, setSelectedDeviceStatus] = useState<string[]>([]);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterCount, setFilterCount] = useState(0);
  const [deviceTypes, setDeviceTypes] = useState<string[]>([]);
  const [filtersChanged, setFiltersChanged] = useState(false);
  // Modal state is now managed in parent (TurnAround.tsx)
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [showEndDateDropdown, setShowEndDateDropdown] = useState(false);
  // Location dropdown state
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  // Fetch device types from API on mount
  useEffect(() => {
    const fetchDeviceTypes = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        const deviceService = new DeviceService(undefined, accessToken);
        const res = await deviceService.getTypes();
        const items = res?.results.map((item: any) => item.name);
        setDeviceTypes(items);
      } catch {
        setDeviceTypes([]);
      }
    };
    fetchDeviceTypes();
  }, [])

  // Flatten locations helper function
  const flattenLocations = (locations: LocationOption[]): LocationOption[] => {
    const result: LocationOption[] = [];
    function recurse(items: LocationOption[]) {
      for (const item of items) {
        result.push(item);
        if (Array.isArray(item.children) && item.children.length > 0) {
          recurse(item.children);
        }
      }
    }
    recurse(locations);
    return result;
  };

  // Fetch locations from API
  useEffect(() => {
    const fetchLocations = async () => {
      if (!selectedSiteId) {
        setLocations([]);
        return;
      }
      setLocationsLoading(true);
      try {
        const accessToken = await getAccessTokenSilently();
        const locationService = new LocationService(userData?.id || '', guid || "", undefined, accessToken);
        const res = await locationService.getAllByGdmNumber();
        const locationData: LocationOption[] = res?.results || [];
        setLocations(flattenLocations(locationData));
      } catch (err) {
        console.error('Error fetching locations:', err);
        setLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    };
    fetchLocations();
  }, [selectedSiteId, getAccessTokenSilently]);


  // Debounce filter update on search
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      triggerFilterUpdate();
    }, 400); // 400ms debounce
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [search]);

  useEffect(() => {
    if (selectedDeviceStatus.length > 0 || selectedStatusOptions.length > 0 || selectedLocations.length > 0 || startDate || endDate) {
      setFiltersChanged(true);
    }
    else {
      setFiltersChanged(false);
    }
  }, [selectedDeviceStatus, selectedStatusOptions, selectedLocations, startDate, endDate])

  const triggerFilterUpdate = () => {
    if (filterDevices) {
      const filters = {
        search,
        status: selectedStatusOptions,
        device_state: selectedDeviceStatus.toString(),
        location: selectedLocations,
        startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
        endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      };
      filterDevices(filters);
      setFiltersChanged(false);
    }
    updateAndLogFilters();
  }

  useEffect(() => {
    // Update filter count based on selected filters
    const dateCount = (startDate ? 1 : 0) + (endDate ? 1 : 0);
    const count = selectedStatusOptions.length + selectedDeviceStatus.length + selectedLocations.length + dateCount;
    setFilterCount(count);
  }, [selectedDeviceStatus, selectedStatusOptions, selectedLocations, startDate, endDate]);

  // Helper for updating and logging filter object
  const updateAndLogFilters = () => {
    setShowStatusDropdown(false);
    setShowDeviceStatus(false);
    setShowDateDropdown(false);
    setShowEndDateDropdown(false);
    setShowLocationDropdown(false);
  };

  // Handlers for multi-select
  const handleStatusOptionChange = (status: string, checked: boolean) => {
    setSelectedStatusOptions(prev => {
      const updated = checked
        ? [...prev, status]
        : prev.filter(s => s !== status);
      return updated;
    });
    setFiltersChanged(true);
  };

  const handleLocationChange = (locationName: string, checked: boolean) => {
    setSelectedLocations(prev => {
      const updated = checked
        ? [...prev, locationName]
        : prev.filter(l => l !== locationName);
      return updated;
    });
    setFiltersChanged(true);
  };

  const clearFilters = () => {
    if (selectedDeviceStatus.length === 0 && selectedStatusOptions.length === 0 && selectedLocations.length === 0 && !startDate && !endDate) {
      return; // No filters to clear
    }
    setSelectedStatusOptions([]);
    setSelectedDeviceStatus([]);
    setSelectedLocations([]);
    setStartDate(null);
    setEndDate(null);
    setFiltersChanged(false);
    updateAndLogFilters();
    if (filterDevices) {
      filterDevices(null);
    }
  }

  const formatLabel = (status: string) => {
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  // Handler for removing chip
  const handleDeleteChip = (type: 'type' | 'location' | 'startDate' | 'endDate', value: string) => {
    if (type === 'type') {
      const updated = selectedStatusOptions.filter(s => s !== value);
      setSelectedStatusOptions(updated);
      if (filterDevices) {
        filterDevices({
          search,
          status: updated,
          location: selectedLocations,
          startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
          endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
        });
      }
    }
    if (type === 'location') {
      const updated = selectedLocations.filter(l => l !== value);
      setSelectedLocations(updated);
      if (filterDevices) {
        filterDevices({
          search,
          status: selectedStatusOptions,
          location: updated,
          startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
          endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
        });
      }
    }
    if (type === 'startDate') {
      setStartDate(null);
      if (filterDevices) {
        filterDevices({
          search,
          status: selectedStatusOptions,
          location: selectedLocations,
          startDate: null,
          endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
        });
      }
    }
    if (type === 'endDate') {
      setEndDate(null);
      if (filterDevices) {
        filterDevices({
          search,
          status: selectedStatusOptions,
          location: selectedLocations,
          startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
          endDate: null,
        });
      }
    }
  };

  const handleStartDateChange = (newValue: Dayjs | null) => {
    setStartDate(newValue);
    if (endDate && newValue && endDate.isBefore(newValue, 'day')) {
      setEndDate(null);
    }
    setFiltersChanged(true);
  };

  const handleEndDateChange = (newValue: Dayjs | null) => {
    if (startDate && newValue && newValue.isBefore(startDate, 'day')) {
      setEndDate(null);
    } else {
      setEndDate(newValue);
      setFiltersChanged(true);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box className="custom-modal">
        <Box className="device-header-container">
          <Box>
            <Typography
              variant="h3"
              className="page-title"
            >
              {t('turnarounds', 'Turnarounds')}
            </Typography>
            <Box className="flex-start">
              <Typography variant="subtitle2" className="site-label">
                {t("site", "Site:")}
              </Typography>
              <Typography variant="body2" className="site-value">
                {(siteName || selectedSiteId) ? (
                  <>
                    <span className="siteName">{siteName}</span>
                    {selectedSiteId && showgdmnumber?.canView && (
                      <>
                        <span className="pipe">|</span>
                        <span className="siteName">
                          {selectedSiteId}
                        </span>
                      </>
                    )}
                  </>
                ) : (
                  t("noSiteSelected", "No Site Selected")
                )}
              </Typography>
            </Box>
          </Box>
          {/* Add Device Button */}
          <Button
            startIcon={<AddIcon />}
            variant="text"
            onClick={onCreateTurnaroundClick}
            className="add-device-button"
          >
            {t('createTurnaround', 'CREATE TURNAROUND')}
          </Button>
        </Box>
        <Box className="search-controls-container">
          <Box className="search-input-container flex-start">
            <TextField
              variant="outlined"
              placeholder={t('turnAroundSearchPlaceholder', 'Search by TurnAround Name')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Typography
            variant="body2"
            className="common-data-grid-header-row-count"
          >
            {paginationModel.page * paginationModel.pageSize + 1}
            - {Math.min((paginationModel.page + 1) * paginationModel.pageSize, totalCount)}
            {t("of", "of")} {totalCount} {t('turnarounds', 'Turnarounds')}
          </Typography>
          {/* 2nd row for mobile: status type and filters; right-aligned row for desktop */}
          <Box className="filter-controls-container">
            {/* Per Page Dropdown */}
            <Box className="common-data-grid-header-right" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography className="common-data-grid-header-right-label-show hide-mobile" sx={{ fontWeight: 500 }}>
                {t("show", "Show:")}
              </Typography>
              <FormControl size="small" className="common-data-grid-header-right-formcontrol">
                <Select
                  value={paginationModel?.pageSize || 100}
                  onChange={e => {
                    if (onPaginationModelChange) {
                      onPaginationModelChange({ ...paginationModel, pageSize: e.target.value as number, page: 0 }, { reason: "pageSizeChange" });
                    }
                  }}
                  displayEmpty
                  className="common-data-grid-header-right-select"
                  inputProps={{ "aria-label": "Show Per Page" }}
                >
                  {[25, 50, 75, 100].map((size) => (
                    <MenuItem key={size} value={size} className="common-data-grid-header-right-menuitem">
                      {size} {t("perPage", "PER PAGE")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box className="filter-controls-container">
            <Box className="filters-toggle">
              <Typography
                variant="body1"
                className="filters-label"
                onClick={() => setFiltersOpen((open) => !open)}
              >
                {t('filters', 'Filters')} {filterCount ? `(${filterCount})` : null}
              </Typography>
              <IconButton
                className="filters-icon-button"
                onClick={() => setFiltersOpen((open) => !open)}
                aria-label={filtersOpen ? 'Close Filters' : 'Open Filters'}
              >
                {filtersOpen ? <CloseIcon /> : <TuneIcon />}
              </IconButton>
            </Box>
            </Box>
          </Box>
        </Box>
        {filtersOpen && (
          <>
            <Box className="filter-chips-container">
              <Typography className="filter-by-label">{t('filterBy', 'Filter by:')}</Typography>
              {/* Custom Chips for selected filters */}
              {selectedStatusOptions.map((type) => (
                <Box
                  key={type}
                  className="filter-chip"
                >
                  <Typography className="filter-chip-text">
                    {type}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteChip('type', type)}
                    className="filter-chip-close"
                    aria-label={`Remove ${type}`}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              {selectedLocations.map((locationName) => (
                <Box key={locationName} className="filter-chip">
                  <Typography className="filter-chip-text">
                    {locationName}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteChip('location', locationName)}
                    className="filter-chip-close"
                    aria-label={`Remove ${locationName}`}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              {/* Chips for selected start and end dates */}
              {startDate && (
                <Box key={startDate.toString()} className="filter-chip">
                  <Typography className="filter-chip-text">
                    {`${t('startLabel', 'Start')}: ${formatDate(startDate.toString())}`}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteChip('startDate', '')}
                    className="filter-chip-close"
                    aria-label={t('chipRemoveStartDate', 'Remove start date')}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              {endDate && (
                <Box key={endDate.toString()} className="filter-chip">
                  <Typography className="filter-chip-text">
                    {`${t('endLabel', 'End')}: ${formatDate(endDate.toString())}`}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteChip('endDate', '')}
                    className="filter-chip-close"
                    aria-label={t('chipRemoveEndDate', 'Remove end date')}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              {selectedDeviceStatus.map((statusKey) => {
                const statusObj = DEVICE_STATUS.find(s => s.key === statusKey);
                return (
                  <Box key={statusKey} className="filter-chip">
                    <Typography className="filter-chip-text">
                      {statusObj ? t(statusObj.labelKey, statusKey) : formatLabel(statusKey)}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setSelectedDeviceStatus(prev => prev.filter(s => s !== statusKey))}
                      className="filter-chip-close"
                      aria-label={`Remove ${statusKey}`}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}
              {/* Clear All Filters: only show as text on md+ */}
              {filterCount > 0 &&
                <Box className="clear-filters-desktop">
                  <Typography
                    className={`clear-filters-text ${filterCount > 0 ? 'clear-filter-text-enable' : ''}`}
                    onClick={() => { clearFilters() }}
                  >
                    {t('clearAllFilters', 'Clear All')}
                  </Typography>
                </Box>}
            </Box>
            {/* Responsive filter controls: stack on xs/sm, row on md+ */}
            <Box className="filter-dropdowns-container">
              {/* Device Type Dropdown */}
              <Box className="filter-dropdown">
                {!showStatusDropdown ? (
                  <Box className="filter-dropdown-collapsed"
                    onClick={() => { setShowStatusDropdown(true); setShowDeviceStatus(false); setShowDateDropdown(false); setShowEndDateDropdown(false); setShowLocationDropdown(false); }}
                  >
                    {t('status', 'STATUS')} ({selectedStatusOptions.length})
                    <ExpandMoreIcon style={{ marginLeft: 4 }} />
                  </Box>
                ) : (
                  <Paper elevation={6} className="filter-dropdown-expanded">
                    <Box className="filter-dropdown-header"
                      onClick={() => setShowStatusDropdown(false)}
                    >
                      {t('status', 'STATUS')} ({selectedStatusOptions.length})
                      <ExpandLessIcon style={{ marginLeft: 4 }} />
                    </Box>
                    <Box className="filter-dropdown-content">
                      <FormGroup>
                        {STATUS_OPTIONS.map((status) => (
                          <FormControlLabel
                            key={status.key}
                            control={
                              <Checkbox
                                checked={selectedStatusOptions.includes(status.key)}
                                onChange={(e) => handleStatusOptionChange(status.key, e.target.checked)}
                                icon={<CustomCheckboxIcon />}
                                checkedIcon={<CustomCheckboxIconChecked tickColor='green' />}
                              />
                            }
                            label={t(status.labelKey, status.key)}
                          />
                        ))}
                      </FormGroup>
                    </Box>
                  </Paper>
                )}
              </Box>


              {/* Start Date Dropdown */}
              <Box className="filter-dropdown">
                {!showDateDropdown ? (
                  <Box className="filter-dropdown-collapsed"
                    onClick={() => { setShowDateDropdown(true); setShowStatusDropdown(false); setShowDeviceStatus(false); setShowEndDateDropdown(false); setShowLocationDropdown(false); }}
                  >
                    {t('startDate', 'START DATE')}
                    <ExpandMoreIcon style={{ marginLeft: 4 }} />
                  </Box>
                ) : (
                  <Paper elevation={6} className="filter-dropdown-expanded">
                    <Box className="filter-dropdown-header"
                      onClick={() => setShowDateDropdown(false)}
                    >
                      {t('startDate', 'START DATE')}
                      <ExpandLessIcon style={{ marginLeft: 4 }} />
                    </Box>
                    <Box className="filter-dropdown-content">
                      <DatePicker
                        label={t('startDate', 'Start Date')}
                        value={startDate}
                        onChange={handleStartDateChange}
                        slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                      />
                    </Box>
                  </Paper>
                )}
              </Box>
              {/* End Date Dropdown */}
              <Box className="filter-dropdown">
                {!showEndDateDropdown ? (
                  <Box className="filter-dropdown-collapsed"
                    onClick={() => { setShowEndDateDropdown(true); setShowStatusDropdown(false); setShowDeviceStatus(false); setShowStatusDropdown(false); setShowLocationDropdown(false); setShowDateDropdown(false) }}
                  >
                    {t('endDate', 'END DATE')}
                    <ExpandMoreIcon style={{ marginLeft: 4 }} />
                  </Box>
                ) : (
                  <Paper elevation={6} className="filter-dropdown-expanded">
                    <Box className="filter-dropdown-header"
                      onClick={() => setShowEndDateDropdown(false)}
                    >
                      {t('endDate', 'END DATE')}
                      <ExpandLessIcon style={{ marginLeft: 4 }} />
                    </Box>
                    <Box className="filter-dropdown-content">
                      <DatePicker
                        label={t('endDate', 'End Date')}
                        value={endDate}
                        minDate={startDate || undefined}
                        disabled={!startDate}
                        onChange={handleEndDateChange}
                        slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                      />
                    </Box>
                  </Paper>
                )}
              </Box>

              {/* Location Dropdown - multi-select, same as Status */}
              <Box className="filter-dropdown">
                {!showLocationDropdown ? (
                  <Box
                    className="filter-dropdown-collapsed"
                    onClick={() => {
                      setShowLocationDropdown(true);
                      setShowStatusDropdown(false);
                      setShowDeviceStatus(false);
                      setShowDateDropdown(false);
                      setShowEndDateDropdown(false);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {t('location', 'LOCATION')} ({selectedLocations.length})
                    <ExpandMoreIcon style={{ marginLeft: 4 }} />
                  </Box>
                ) : (
                  <Paper elevation={6} className="filter-dropdown-expanded">
                    <Box className="filter-dropdown-header"
                      onClick={() => setShowLocationDropdown(false)}
                    >
                      {t('location', 'LOCATION')} ({selectedLocations.length})
                      <ExpandLessIcon style={{ marginLeft: 4 }} />
                    </Box>
                    <Box className="filter-dropdown-content">
                      {locationsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : locations.length === 0 ? (
                        <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                          {t('noLocationsAvailable', 'No locations available')}
                        </Typography>
                      ) : (
                        <FormGroup>
                          {locations.map((location) => (
                            <FormControlLabel
                              key={location.id}
                              control={
                                <Checkbox
                                  checked={selectedLocations.includes(location.name || '')}
                                  onChange={(e) => handleLocationChange(location.name || '', e.target.checked)}
                                  icon={<CustomCheckboxIcon />}
                                  checkedIcon={<CustomCheckboxIconChecked tickColor='green' />}
                                />
                              }
                              label={location.name}
                            />
                          ))}
                        </FormGroup>
                      )}
                    </Box>
                  </Paper>
                )}
              </Box>
              {/* Apply Filters Button: only show in row on md+ */}
              <Box className="apply-filters-desktop">
                <Button
                  variant="contained"
                  className={`apply-filters-button ${filtersChanged ? 'active' : 'inactive'}`}
                  onClick={() => triggerFilterUpdate()}
                  disabled={!filtersChanged}
                >
                  {t('applyFilters', 'APPLY FILTERS')}
                </Button>
              </Box>
            </Box>
            {/* Apply Filters Button: full width on xs/sm */}
            <Box className="apply-filters-mobile-container">
              <Button
                variant="contained"
                className="apply-filters-mobile"
                onClick={() => triggerFilterUpdate()}
              >
                {t('applyFilters', 'APPLY FILTERS')}
              </Button>
            </Box>
            {/* Clear All Filters: full width button on xs/sm */}
            {filterCount > 0 &&
              <Box className="clear-filters-mobile-container">
                <Button
                  variant="outlined"
                  className="clear-filters-mobile"
                  onClick={() => clearFilters()}
                >
                  {t('clearAllFilters', 'Clear All')}
                </Button>
              </Box>}
          </>
        )}
        <Divider className="header-divider" />
        {/* Modal is now managed in parent TurnAround.tsx */}
      </Box>
    </LocalizationProvider>
  );
});

export default TurnAroundHeader;
